import { useState, useCallback, useEffect } from "react";
import { PresenceService } from "../services/PresenceService";

export const useHookPresence = () => {
    // --- State untuk Tabel History ---
    const [data, setData] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [params, setParams] = useState({
        month: new Date().toISOString().slice(0, 7), // Default bulan ini (YYYY-MM)
        page: 1,
        per_page: 10,
    });

    // --- State untuk Status Aktif (Sudah absen masuk atau belum) ---
    const [activeStatus, setActiveStatus] = useState({
        is_clocked_in: false,
        clock_in_time: null,
        data: null,
    });
    const [loadingActiveStatus, setLoadingActiveStatus] = useState(false);

    // --- State untuk Statistik Bulanan ---
    const [monthlyStats, setMonthlyStats] = useState({
        kehadiran: 0,
        terlambat: 0,
        izin: 0,
        alpha: 0,
        total_jam: 0,
    });

    // --- State untuk Loading Aksi Tombol ---
    const [loadingAction, setLoadingAction] = useState(false);

    /* ============================================
       FETCH FUNCTIONS
       ============================================ */

    // 1. Fetch History Data (Tabel)
    const getHistory = useCallback(async () => {
        setLoading(true);
        setError(null);

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined)
        );

        try {
            const res = await PresenceService.get({ params: cleanParams });
            const result = res?.data?.data ?? [];
            setData(Array.isArray(result) ? result : []);
            setMeta(res?.data?.meta ?? null);
        } catch (err) {
            console.error(err);
            setError(err?.response?.data || err);
        } finally {
            setLoading(false);
        }
    }, [params]);

    // 2. Fetch Active Status (Cek sudah clock in atau belum)
    const fetchActiveStatus = useCallback(async () => {
        setLoadingActiveStatus(true);
        try {
            const res = await PresenceService.getActiveStatus();
            setActiveStatus(res?.data ?? { is_clocked_in: false, clock_in_time: null });
        } catch (err) {
            console.error("Gagal memuat status aktif:", err);
        } finally {
            setLoadingActiveStatus(false);
        }
    }, []);

    // 3. Fetch Monthly Stats (Kartu Statistik)
    const fetchMonthlyStats = useCallback(async (period) => {
        try {
            const res = await PresenceService.getMonthlyStats({ 
                params: { period: period || params.month } 
            });
            setMonthlyStats(res?.data ?? { kehadiran: 0, terlambat: 0, izin: 0, alpha: 0, total_jam: 0 });
        } catch (err) {
            console.error("Gagal memuat statistik bulanan:", err);
        }
    }, [params.month]);

    /* ============================================
       ACTION FUNCTIONS (CRUD)
       ============================================ */

    const handleClockIn = useCallback(async (payload = {}) => {
        setLoadingAction(true);
        try {
            await PresenceService.clockIn({ data: payload });
            
            // Refresh data setelah berhasil
            await fetchActiveStatus();
            await getHistory();
            
            return true; // Return true sebagai tanda sukses
        } catch (err) {
            throw err; // Lempar error agar bisa ditangkap UI (untuk Toast)
        } finally {
            setLoadingAction(false);
        }
    }, [fetchActiveStatus, getHistory]);

    const handleClockOut = useCallback(async (payload = {}) => {
        setLoadingAction(true);
        try {
            await PresenceService.clockOut({ data: payload });
            
            // Refresh semua data karena ada perubahan status & rekap bulanan
            await fetchActiveStatus();
            await fetchMonthlyStats();
            await getHistory();
            
            return true;
        } catch (err) {
            throw err;
        } finally {
            setLoadingAction(false);
        }
    }, [fetchActiveStatus, fetchMonthlyStats, getHistory]);

    /* ============================================
       EFFECTS
       ============================================ */

    // Load active status saat pertama kali mount
    useEffect(() => {
        fetchActiveStatus();
    }, [fetchActiveStatus]);

    // Load stats saat bulan berubah
    useEffect(() => {
        fetchMonthlyStats();
    }, [fetchMonthlyStats]);

    // Load history saat parameter berubah (page, month, dll)
    useEffect(() => {
        getHistory();
    }, [getHistory]);

    /* ============================================
       RETURN VALUES
       ============================================ */

    return {
        // History Table
        data,
        meta,
        loading,
        error,
        params,
        setParams,
        refreshHistory: getHistory,

        // Active Status (Clock In/Out state)
        activeStatus,
        loadingActiveStatus,

        // Monthly Stats
        monthlyStats,

        // Actions
        loadingAction,
        handleClockIn,
        handleClockOut,
    };
};